import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Switch } from 'components/ui/switch';
import { Badge } from 'components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import {
  ArrowLeft, Bell, Mail, MessageSquare, Smartphone, Clock, Shield,
  DollarSign, Heart, Users, AlertTriangle, Calendar, Loader2, CheckCircle, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const NOTIFICATION_GROUPS = [
  {
    id: 'channels',
    title: 'Delivery Channels',
    description: 'How you receive notifications',
    icon: Bell,
    color: 'text-teal-500 bg-teal-50',
    items: [
      { key: 'push_notifications', label: 'In-App Notifications', desc: 'Bell icon alerts in the app', icon: Bell },
      { key: 'email_notifications', label: 'Email Notifications', desc: 'Receive alerts to your email', icon: Mail },
      { key: 'sms_notifications', label: 'SMS Notifications', desc: 'Text message alerts (requires phone)', icon: Smartphone },
    ]
  },
  {
    id: 'shifts',
    title: 'Shifts & Scheduling',
    description: 'Clock-in reminders, approvals, schedule changes',
    icon: Clock,
    color: 'text-blue-500 bg-blue-50',
    items: [
      { key: 'shift_reminders', label: 'Shift Reminders', desc: 'Reminders before upcoming shifts', icon: Clock },
      { key: 'approval_notifications', label: 'Approval Alerts', desc: 'When shifts are approved or flagged', icon: CheckCircle },
      { key: 'schedule_change_alerts', label: 'Schedule Changes', desc: 'When shifts are rescheduled or modified', icon: Calendar },
      { key: 'time_off_alerts', label: 'Time-Off Updates', desc: 'Requests and approvals for time off', icon: Calendar },
    ]
  },
  {
    id: 'care',
    title: 'Care & Safety',
    description: 'Health alerts, emergency backups, EVV compliance',
    icon: Heart,
    color: 'text-rose-500 bg-rose-50',
    items: [
      { key: 'health_alerts', label: 'Health Alerts', desc: 'Critical health metric warnings', icon: Heart },
      { key: 'emergency_backup_alerts', label: 'Emergency Backup', desc: 'When backup coverage is needed or found', icon: Zap },
      { key: 'caregiver_cancellations', label: 'Caregiver Cancellations', desc: 'When a caregiver cancels their shift', icon: AlertTriangle },
      { key: 'evv_anomaly_alerts', label: 'EVV Anomalies', desc: 'Compliance issues and visit verification problems', icon: Shield },
      { key: 'stress_checkin_reminders', label: 'Stress Check-in Reminders', desc: 'Periodic wellness check reminders', icon: Heart },
    ]
  },
  {
    id: 'team',
    title: 'Team & Communication',
    description: 'Messages, invitations, caregiver requests',
    icon: Users,
    color: 'text-purple-500 bg-purple-50',
    items: [
      { key: 'message_notifications', label: 'Messages', desc: 'New messages in care circles', icon: MessageSquare },
      { key: 'caregiver_invitation_alerts', label: 'Invitations & Requests', desc: 'Caregiver invites and care requests', icon: Users },
      { key: 'task_reminders', label: 'Task Reminders', desc: 'Task assignments and due date reminders', icon: CheckCircle },
    ]
  },
  {
    id: 'financial',
    title: 'Financial',
    description: 'Budget alerts, expense tracking, weekly summaries',
    icon: DollarSign,
    color: 'text-emerald-500 bg-emerald-50',
    items: [
      { key: 'budget_alerts', label: 'Budget Threshold Alerts', desc: 'When spending approaches budget limits', icon: DollarSign },
      { key: 'expense_alerts', label: 'Expense Notifications', desc: 'New expenses added to your care circle', icon: DollarSign },
      { key: 'weekly_summary', label: 'Weekly Summary', desc: 'End-of-week hours and spending report', icon: Mail },
    ]
  },
];

export default function NotificationPreferences() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/push/settings');
      setSettings(response.data.settings || {});
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    const prev = settings[key];
    setSettings(s => ({ ...s, [key]: value }));
    try {
      await api.put('/push/settings', { [key]: value });
    } catch (error) {
      setSettings(s => ({ ...s, [key]: prev }));
      toast.error('Failed to update setting');
    }
  };

  const enableAll = async () => {
    setSaving(true);
    const allOn = {};
    NOTIFICATION_GROUPS.forEach(g => g.items.forEach(i => { allOn[i.key] = true; }));
    setSettings(s => ({ ...s, ...allOn }));
    try {
      await api.put('/push/settings', allOn);
      toast.success('All notifications enabled');
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const muteAll = async () => {
    setSaving(true);
    const allOff = {};
    NOTIFICATION_GROUPS.filter(g => g.id !== 'channels').forEach(g =>
      g.items.forEach(i => { allOff[i.key] = false; })
    );
    setSettings(s => ({ ...s, ...allOff }));
    try {
      await api.put('/push/settings', allOff);
      toast.success('Non-critical notifications muted');
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notification-preferences">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="back-to-settings">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Notification Preferences</h1>
              <p className="text-xs sm:text-sm text-slate-500">Choose what alerts you receive and how they're delivered</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={muteAll} disabled={saving} className="shrink-0 text-slate-900 border-slate-300" data-testid="mute-all-btn">
              <span className="hidden sm:inline">Mute</span><span className="sm:hidden">Mute</span> All
            </Button>
            <Button size="sm" onClick={enableAll} disabled={saving} className="shrink-0 bg-teal-500 hover:bg-teal-600" data-testid="enable-all-btn">
              <span className="hidden sm:inline">Enable</span><span className="sm:hidden">Enable</span> All
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6 space-y-6">
        {/* Critical notice */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Critical Safety Alerts</p>
            <p className="text-sm text-amber-700">Health emergencies, caregiver cancellations, and backup requests are always delivered regardless of your settings.</p>
          </div>
        </div>

        {NOTIFICATION_GROUPS.map(group => {
          const GroupIcon = group.icon;
          return (
            <Card key={group.id} data-testid={`notification-group-${group.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${group.color}`}>
                    <GroupIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{group.title}</CardTitle>
                    <CardDescription>{group.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-1">
                {group.items.map(item => {
                  const ItemIcon = item.icon;
                  const isEnabled = settings[item.key] !== false;
                  return (
                    <div
                      key={item.key}
                      className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`pref-${item.key}`}
                    >
                      <div className="flex items-center gap-3">
                        <ItemIcon className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{item.label}</p>
                          <p className="text-xs text-slate-500">{item.desc}</p>
                        </div>
                      </div>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(v) => updateSetting(item.key, v)}
                        data-testid={`switch-${item.key}`}
                      />
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}

        {/* Shift Reminder Time */}
        <Card data-testid="reminder-time-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Reminder Timing</CardTitle>
            <CardDescription>How early to send shift reminders</CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={String(settings.shift_reminder_time || 30)}
              onValueChange={(v) => updateSetting('shift_reminder_time', parseInt(v))}
            >
              <SelectTrigger className="w-full sm:w-48" data-testid="reminder-time-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutes before</SelectItem>
                <SelectItem value="30">30 minutes before</SelectItem>
                <SelectItem value="60">1 hour before</SelectItem>
                <SelectItem value="120">2 hours before</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
